import React, { useState } from "react";
import {
  CircularProgress,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { DeleteOutlined } from "@mui/icons-material";
import { Button, ButtonCategory } from "./button/Button";

export interface IActionAndConfirmProps {
  onRemove: () => Promise<any>;
  errorMessage?: string;
  isConfirmDisabled?: boolean;
  loading: boolean;
  confirmTitle: string;
  confirmContent: string;
  label?: string;
  children?: React.ReactElement;
  dialogChildren?: React.ReactNode;
  iconOnly?: boolean;
  onOpenModal?: () => void;
  openDialog?: boolean;
  onRemoveCompleted?: () => void;
}
export const ActionAndConfirm: React.FC<IActionAndConfirmProps> = ({
  isConfirmDisabled,
  onRemove,
  errorMessage,
  loading,
  children,
  dialogChildren,
  confirmTitle,
  confirmContent,
  label,
  iconOnly,
  onOpenModal,
  openDialog,
  onRemoveCompleted,
}) => {
  const [open, setOpen] = useState(openDialog || false);
  const handleOpen = () => {
    setOpen(true);
    onOpenModal?.();
  };
  const handleRemove = () => {
    onRemove().then((res) => {
      if (!res?.error) {
        setOpen(false);
        onRemoveCompleted?.();
      }
    });
  };
  return (
    <>
      {!openDialog && (
        <>
          {children ? (
            React.cloneElement(children, {
              onClick: () => {
                children.props.onClick?.();
                handleOpen();
              },
            })
          ) : (
            <IconButton
              size="small"
              onClick={() => {
                setOpen(true);
                onOpenModal?.();
              }}
              aria-label={label || "remove"}
              title={label || "remove"}
            >
              {loading ? (
                <CircularProgress
                  aria-label="loading"
                  size="16px"
                  color="inherit"
                />
              ) : (
                <DeleteOutlined
                  fontSize="small"
                  sx={{ color: iconOnly ? "error.main" : "inherit" }}
                />
              )}
            </IconButton>
          )}
        </>
      )}
      {errorMessage && (
        <Typography color="error" variant="caption">
          {errorMessage}
        </Typography>
      )}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="alert-dialog-confirmTitle"
        aria-describedby="alert-dialog-description"
        maxWidth={"sm"}
      >
        <DialogTitle
          sx={{ textAlign: "start", pb: 0.5 }}
          variant="h6"
          id="alert-dialog-confirmTitle"
        >
          {confirmTitle}
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <DialogContentText
            sx={{ textAlign: "start", color: "text.secondary" }}
            id="alert-dialog-description"
            variant="body2"
          >
            {confirmContent}
          </DialogContentText>
          {dialogChildren}
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end",
            mb: 2,
            pr: 3,
            gap: 1,
          }}
        >
          <Button
            onClick={() => setOpen(false)}
            category={ButtonCategory.Secondary}
          >
            Cancel
          </Button>
          <Button
            category={ButtonCategory.Primary}
            data-cy="confirm"
            disabled={isConfirmDisabled || !!errorMessage || loading}
            endIcon={
              (isConfirmDisabled || loading) && (
                <CircularProgress size="16px" color="inherit" />
              )
            }
            onClick={handleRemove}
          >
            Confirm
          </Button>
          {errorMessage && (
            <Typography color="error" variant="caption">
              {errorMessage}
            </Typography>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
